import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Delete, Restore, Warning } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  deleteSiteById,
  permanentlyDeleteSiteById,
} from "../redux/slices/siteSlice";

/**
 * Soft Delete Banner - Shows at top of page when site is deleted
 */
export const SoftDeleteBanner = ({ site, onRestore, loading = false }) => {
  if (!site || site.status !== "DELETED") return null;

  const canRestoreUntil = site.metadata?.canRestoreUntil
    ? new Date(site.metadata.canRestoreUntil)
    : null;

  const daysRemaining = canRestoreUntil
    ? Math.ceil((canRestoreUntil - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  const isPastRestoreDate = daysRemaining <= 0;

  return (
    <Alert
      severity={isPastRestoreDate ? "error" : "warning"}
      sx={{ mb: 3 }}
      action={
        !isPastRestoreDate && (
          <Button
            color="inherit"
            size="small"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Restore />
              )
            }
            onClick={onRestore}
            disabled={loading}
          >
            {loading ? "Restoring..." : "Restore Site"}
          </Button>
        )
      }
    >
      <Typography variant="body1" fontWeight={600}>
        {isPastRestoreDate
          ? "This site has been permanently deleted"
          : "This site has been soft deleted"}
      </Typography>
      <Typography variant="body2">
        {isPastRestoreDate
          ? "This site can no longer be restored."
          : `You can restore this site within ${daysRemaining} day${
              daysRemaining !== 1 ? "s" : ""
            }. 
             After ${canRestoreUntil?.toLocaleDateString()}, it will be permanently deleted.`}
      </Typography>
    </Alert>
  );
};

/**
 * Soft Delete Dialog - Confirmation dialog for soft delete
 */
export const SoftDeleteDialog = ({
  open,
  onClose,
  site,
  onConfirm,
  loading,
}) => {
  const [confirmText, setConfirmText] = useState("");

  const handleConfirm = () => {
    if (confirmText === "DELETE") {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Soft Delete Site</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete <strong>"{site?.name}"</strong>?
        </Typography>

        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            This is a soft delete - your site will not be immediately removed.
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            <li>Site will be marked as DELETED</li>
            <li>You can restore it within 30 days</li>
            <li>After 30 days, it will be permanently deleted</li>
            <li>A backup will be created before deletion</li>
          </ul>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Type <strong>DELETE</strong> to confirm:
          </Typography>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginTop: "8px",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="warning"
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || confirmText !== "DELETE"}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}
        >
          {loading ? "Deleting..." : "Soft Delete Site"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Permanent Delete Dialog - Confirmation for permanent deletion
 */
export const PermanentDeleteDialog = ({
  open,
  onClose,
  site,
  onConfirm,
  loading,
}) => {
  const [confirmText, setConfirmText] = useState("");

  const handleConfirm = () => {
    if (confirmText === "PERMANENT DELETE") {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Delete color="error" />
          <Typography variant="h6">Permanently Delete Site</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to <strong>permanently delete</strong> "
          {site?.name}"?
        </Typography>

        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            THIS ACTION CANNOT BE UNDONE!
          </Typography>
          <Typography variant="body2">This will permanently delete:</Typography>
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            <li>All WordPress files and folders</li>
            <li>Site database ({site?.dbName})</li>
            <li>All backups and logs</li>
            <li>Nginx configuration</li>
            <li>Site settings and history</li>
          </ul>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Type <strong>PERMANENT DELETE</strong> to confirm:
          </Typography>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type PERMANENT DELETE"
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #d32f2f",
              borderRadius: "4px",
              marginTop: "8px",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || confirmText !== "PERMANENT DELETE"}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}
        >
          {loading ? "Deleting..." : "Delete Permanently"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Danger Zone Component - Shows delete options based on site status
 */
export const DangerZone = ({ site, siteId }) => {
  const dispatch = useDispatch();
  const [softDeleteOpen, setSoftDeleteOpen] = useState(false);
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isDeleted = site?.status === "DELETED";
  const canRestoreUntil = site?.metadata?.canRestoreUntil
    ? new Date(site?.metadata.canRestoreUntil)
    : null;
  const isPastRestoreDate = canRestoreUntil && new Date() > canRestoreUntil;

  const handleSoftDelete = async () => {
    setLoading(true);
    setError("");
    try {
      await dispatch(deleteSiteById(siteId)).unwrap();
      setSoftDeleteOpen(false);
      // Optional: Show success message
    } catch (err) {
      setError(
        err?.error ||
          err?.message ||
          (typeof err === "string" ? err : "Failed to delete site"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    setLoading(true);
    setError("");
    try {
      await dispatch(permanentlyDeleteSiteById(siteId)).unwrap();
      setPermanentDeleteOpen(false);
      // Navigate away or show success
    } catch (err) {
      setError(
        err?.error ||
          err?.message ||
          (typeof err === "string" ? err : "Failed to permanently delete site"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} color="error" gutterBottom>
        Danger Zone
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "error.main",
          borderRadius: 2,
          mt: 2,
        }}
      >
        {!isDeleted ? (
          <>
            <Typography variant="body2" gutterBottom>
              Soft delete this site. You'll have 30 days to restore it before
              permanent deletion.
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Delete />}
              sx={{ mt: 2 }}
              onClick={() => setSoftDeleteOpen(true)}
            >
              Soft Delete This Site
            </Button>
          </>
        ) : !isPastRestoreDate ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This site is soft deleted. You can restore it until{" "}
                {canRestoreUntil?.toLocaleDateString()}
              </Typography>
            </Alert>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setPermanentDeleteOpen(true)}
            >
              Delete Permanently Now
            </Button>
          </>
        ) : (
          <Alert severity="error">
            <Typography variant="body2">
              This site's restoration period has expired. It can be permanently
              deleted.
            </Typography>
          </Alert>
        )}
      </Box>

      <SoftDeleteDialog
        open={softDeleteOpen}
        onClose={() => setSoftDeleteOpen(false)}
        site={site}
        onConfirm={handleSoftDelete}
        loading={loading}
      />

      <PermanentDeleteDialog
        open={permanentDeleteOpen}
        onClose={() => setPermanentDeleteOpen(false)}
        site={site}
        onConfirm={handlePermanentDelete}
        loading={loading}
      />
    </>
  );
};

const SiteDetailExtras = {
  SoftDeleteBanner,
  SoftDeleteDialog,
  PermanentDeleteDialog,
  DangerZone,
};

export default SiteDetailExtras;
